import useStore, { DEBUG } from 'state/knovStore'
import { setManualSlide } from 'components/PanelController/PanelController'
import { isMobile } from 'lib/device'
import {
    getCenterIndex,
    getSwiperRef,
    slideTo as slideToPanel,
    SWIPE_SPEED_FAST,
    SWIPE_SPEED_SLOW,
} from 'state/imperativeApis/swiperApi'
import { v4 as uuid } from 'uuid'
import { Filter } from 'components/filters/useStreamFilters'
import { flushSync } from 'react-dom'
import {
    pausePanelMediaByPanelId,
    preparePanelMediaHandoffToAnswer,
    resumePanelMediaHandoff,
} from 'components/panels/panelMediaPlayback'
import { findAnswerTargetElement, isAnswerElementNearTarget } from 'lib/answerTargetScroll'
import { findAnswerHighlightElement } from 'lib/answerHighlightTargets'
import { userHasFeature } from 'refactor/hooks/useUserHasFeature'
import { prepareOpenRight } from 'refactor/panels/prepareOpenRight'
import { recordEngagementOpen } from 'lib/questImpressions'
import { getCachedQuest } from 'state/cache'
import { panelShowsTarget } from 'lib/panelUpTarget'
import {
    markPanelNavMetric,
    markPanelNavQuestSettled,
    markPanelNavTransitionEnd,
    startPanelNavMetric,
} from 'lib/panelNavMetrics'
import type { PanelNavInputDetail } from 'lib/panelNavMetrics'

type OpenRightCoreOptions = {
    anchorPanelId: string
    filter: Filter
    panelId?: string
    meta?: Record<string, unknown>
    metricInput?: PanelNavInputDetail
    ensureUnique?: boolean
    suppressAutoReveal?: boolean
    onPanelReady?: (panelId: string) => void
}

export async function openRightCore({
    anchorPanelId,
    filter,
    panelId,
    meta,
    metricInput,
    ensureUnique = true,
    suppressAutoReveal = false,
    onPanelReady,
}: OpenRightCoreOptions) {
    const panels = useStore.getState().panels
    const numPanels = useStore.getState().numPanels
    const swiper = getSwiperRef()?.current
    const instantReplyNavEnabled = userHasFeature('instant_reply_nav')
    const routeOwnsInstantAnswerFeedback =
        instantReplyNavEnabled && Boolean(filter?.questId && filter?.answerId)
    // Start the quest fetch at click time so network overlaps the panel insert/swipe and a
    // fresh-but-target-less cache self-heals before the reveal deadline.
    if (filter?.questId && filter?.answerId && !suppressAutoReveal) {
        void prepareOpenRight(filter.questId, filter.answerId).catch(() => null)
    }

    // Click-through for the engagement log. Every thread opened from a card
    // funnels through here, so this is the one place that pairs an open with the
    // exposure that produced it. The anchor panel's filter is the surface the
    // user clicked FROM; the cached quest supplies its actual space even if a
    // click beats IntersectionObserver's first callback. Metrics only — never
    // allowed to affect navigation.
    if (filter?.questId) {
        try {
            const questId = String(filter.questId)
            recordEngagementOpen(questId, {
                filter: panels.getPanel(anchorPanelId)?.filter,
                spaceId: getCachedQuest(questId)?.space_id,
            })
        } catch {}
    }

    const getIndexById = panels.getIndexById
    const indexBeforeChange = getIndexById(anchorPanelId)
    const clickedRightPanel = indexBeforeChange > getCenterIndex()
    const shouldSlide = isMobile || numPanels === 1 || clickedRightPanel
    // The coordinator verifies that the playing media belongs to this exact post,
    // covering both child-thread opens and same-message reply/permalink opens.
    const mediaHandoffKey = filter?.answerId
        ? preparePanelMediaHandoffToAnswer(anchorPanelId, filter.answerId)
        : null
    startPanelNavMetric('open-right', {
        anchorPanelId,
        requestedPanelId: panelId,
        filter,
        meta,
        ...metricInput,
        ensureUnique,
        suppressAutoReveal,
        indexBeforeChange,
        centerIndex: getCenterIndex(),
        clickedRightPanel,
        shouldSlide,
        numPanels,
        isMobile,
    })
    const prepareAnchorPanelDocking = () => {
        if (mediaHandoffKey) return
        if (isMobile || numPanels === 1) {
            pausePanelMediaByPanelId(anchorPanelId)
        }
    }
    const notifyPanelReady = (targetPanelId: string) => {
        onPanelReady?.(targetPanelId)
    }

    const waitAndShake = (targetPanelId: string) => {
        let attempts = 0
        const maxAttempts = 20
        const poll = () => {
            const p = panels.getPanel(targetPanelId)
            if (p && !p.empty) {
                p.shake?.()
            } else if (attempts++ < maxAttempts) {
                setTimeout(poll, 150)
            }
        }
        poll()
    }

    const waitForAnswerFeedback = (targetPanelId: string, answerId: string) => {
        let attempts = 0
        const maxAttempts = 60
        const poll = () => {
            const targetPanel = panels.getPanel(targetPanelId)
            if (String(targetPanel?.filter?.answerId || '') !== String(answerId)) return

            const scrollContainer = targetPanel?.getScrollContainer?.()
            const answerElement = findAnswerTargetElement(scrollContainer, answerId)
            if (
                targetPanel &&
                scrollContainer &&
                answerElement &&
                isAnswerElementNearTarget(scrollContainer, answerElement)
            ) {
                // The highlight lands on the .answer-highlight surface, not on
                // the row this poll located — checking the row could never see
                // it, so this backstop always fired and flashed a second time.
                const highlightElement = findAnswerHighlightElement(scrollContainer, answerId)
                if (!highlightElement?.classList.contains('highlight')) {
                    targetPanel.highlightAnswer?.(answerId)
                }
            } else if (attempts++ < maxAttempts) {
                setTimeout(poll, 150)
            }
        }
        poll()
    }

    const prepareTargetPanel = (targetPanelId: string, ensureRouteIntent = false) => {
        const targetPanel = panels.getPanel(targetPanelId)
        const readinessSource = `open-right:${uuid()}`
        const waitsForTransition = Boolean(shouldSlide && swiper)
        const releaseReadiness = waitsForTransition
            ? targetPanel?.blockAnswerReveal?.(readinessSource) || (() => {})
            : () => {}
        let completed = false
        let fallbackTimeout: ReturnType<typeof setTimeout> | null = null

        const finish = () => {
            if (completed) return
            completed = true
            if (fallbackTimeout) clearTimeout(fallbackTimeout)
            releaseReadiness()
            if (mediaHandoffKey) {
                resumePanelMediaHandoff(targetPanelId, mediaHandoffKey)
            }

            if (suppressAutoReveal) notifyPanelReady(targetPanelId)
            else if (!filter?.answerId) waitAndShake(targetPanelId)
            // QuestContainer owns the instant route reveal and its single highlight.
            else if (!routeOwnsInstantAnswerFeedback)
                waitForAnswerFeedback(targetPanelId, filter.answerId)
        }

        // Submit before React's route ensure. Both entry points then share this one
        // coordinator transaction; transition completion only releases readiness.
        if (!suppressAutoReveal && filter?.answerId) {
            const submitReveal = ensureRouteIntent
                ? targetPanel?.ensureAnswerReveal
                : targetPanel?.requestAnswerReveal
            void submitReveal?.(filter.answerId, {
                questId: filter.questId,
                contextScroll: true,
                highlight: true,
            })
        }

        if (waitsForTransition) {
            fallbackTimeout = setTimeout(
                finish,
                (!isMobile && numPanels === 1 ? SWIPE_SPEED_SLOW : SWIPE_SPEED_FAST) + 150,
            )
        }

        return finish
    }

    // Dedupe for questId: if target quest already in panel to right, update answerId and act
    if (ensureUnique && filter?.questId) {
        const right = panels.getPanelToRight(anchorPanelId)
        if (right && !right.empty && right.filter?.questId === filter.questId) {
            if (filter?.answerId) {
                panels.editPanel(right.panelId, {
                    filter: { ...right.filter, answerId: filter.answerId },
                    ...(right.pendingOpenPlacement?.contextScroll
                        ? {
                              pendingOpenPlacement: {
                                  questId: String(filter.questId),
                                  answerId: String(filter.answerId),
                                  contextScroll: true,
                              },
                          }
                        : {}),
                })
            }
            markPanelNavQuestSettled({
                panelId: right.panelId,
                questId: filter.questId,
                hasDisplayQuest: true,
                reusedPanel: true,
            })
            // Reused panels need a fresh request for each click, including repeated
            // clicks on the same post. The route ensure joins it instead of replaying it.
            const finishTargetPanel = prepareTargetPanel(right.panelId)
            if (shouldSlide && swiper) {
                prepareAnchorPanelDocking()
                const fn = () => {
                    // The generic transition-end metric carries the CENTER panel's id, which
                    // the target Panel ignores — emit one addressed to the target so it can
                    // mount content and release reveal readiness at the true transition end
                    // instead of the fallback timer.
                    if (instantReplyNavEnabled) {
                        markPanelNavTransitionEnd({
                            source: 'openRightSlideEnd',
                            targetPanelId: right.panelId,
                            filter,
                        })
                    }
                    finishTargetPanel()
                    swiper.off && swiper.off('slideChangeTransitionEnd', fn)
                }
                swiper.once
                    ? swiper.once('slideChangeTransitionEnd', fn)
                    : swiper.on?.('slideChangeTransitionEnd', fn)
                if (!isMobile && numPanels !== 1 && clickedRightPanel) {
                    // We programmatically control the slide; mark as manual so transforms animate smoothly
                    try {
                        setManualSlide(true)
                    } catch {}
                    markPanelNavMetric('slide-requested', {
                        method: 'slideNext',
                        targetPanelId: right.panelId,
                        filter,
                    })
                    swiper.slideNext(SWIPE_SPEED_FAST)
                } else {
                    const targetIndex = panels.getIndexById(right.panelId)
                    const targetSwiperIndex =
                        numPanels === 1 || isMobile ? targetIndex : Math.max(0, targetIndex - 1)
                    // In one-panel/mobile, defer until React/Swiper renders the new slide
                    if (numPanels === 1 || isMobile) {
                        requestAnimationFrame(() => {
                            try {
                                swiper.virtual &&
                                    swiper.virtual.update &&
                                    swiper.virtual.update(true)
                            } catch {}
                            // Mark as manual for smooth transform animation
                            try {
                                setManualSlide(true)
                            } catch {}
                            markPanelNavMetric('slide-requested', {
                                method: 'slideTo',
                                targetPanelId: right.panelId,
                                targetIndex,
                                targetSwiperIndex,
                                filter,
                                deferred: true,
                            })
                            if (numPanels === 1 && !isMobile) {
                                slideToPanel(targetIndex, SWIPE_SPEED_SLOW)
                            } else {
                                swiper.slideTo(targetSwiperIndex, SWIPE_SPEED_FAST)
                            }
                        })
                    } else {
                        try {
                            setManualSlide(true)
                        } catch {}
                        markPanelNavMetric('slide-requested', {
                            method: 'slideTo',
                            targetPanelId: right.panelId,
                            targetIndex,
                            targetSwiperIndex,
                            filter,
                        })
                        swiper.slideTo(targetSwiperIndex, SWIPE_SPEED_FAST)
                    }
                }
            } else {
                markPanelNavMetric('slide-skipped', {
                    reason: shouldSlide ? 'missing-swiper' : 'no-slide-needed',
                    targetPanelId: right.panelId,
                    filter,
                })
                markPanelNavTransitionEnd({
                    source: 'openRightNoSwiperTransition',
                    targetPanelId: right.panelId,
                    filter,
                })
                finishTargetPanel()
            }
            return { panelId: right.panelId, existed: true as const }
        }
    }

    // Dedupe for streams (a profile, a channel…): the same stream already open
    // in the panel to the right is shown and shaken instead of opened twice.
    if (ensureUnique && !filter?.questId) {
        const right = panels.getPanelToRight(anchorPanelId)
        if (right && !right.empty && panelShowsTarget(right.filter, { filter, label: '' })) {
            markPanelNavMetric('slide-skipped', {
                reason: 'existing-stream-panel',
                targetPanelId: right.panelId,
                filter,
            })
            if (shouldSlide && swiper) {
                prepareAnchorPanelDocking()
                try {
                    setManualSlide(true)
                } catch {}
                const targetIndex = panels.getIndexById(right.panelId)
                if (numPanels === 1 && !isMobile) slideToPanel(targetIndex, SWIPE_SPEED_SLOW)
                else
                    swiper.slideTo(
                        isMobile ? targetIndex : Math.max(0, targetIndex - 1),
                        SWIPE_SPEED_FAST,
                    )
            }
            waitAndShake(right.panelId)
            return { panelId: right.panelId, existed: true as const }
        }
    }

    // Insert new panel to the right
    const newPanelId = panelId || uuid()
    const shouldAnimatePanelShift = !isMobile && numPanels !== 1 && !clickedRightPanel
    const openRightFromLeft = !clickedRightPanel
    const prevCenterPanelId = panels.getPanels()[getCenterIndex()]?.panelId
    if (DEBUG()) {
        // eslint-disable-next-line no-console
        console.log('[openRightCore] start', {
            anchorPanelId,
            clickedRightPanel,
            openRightFromLeft,
            prevCenterPanelId,
            numPanels,
            centerIndex: getCenterIndex(),
        })
    }

    let targetPanelId = newPanelId
    // Fresh panels prepare and stage behind the paint gate. Reused visible panels
    // hold their current viewport while preparing the same short context reveal.
    const placementIntent =
        filter?.questId && filter?.answerId && !suppressAutoReveal
            ? {
                  questId: String(filter.questId),
                  answerId: String(filter.answerId),
                  contextScroll: true,
              }
            : undefined
    const insertPanel = () => {
        markPanelNavMetric('panel-insert-start', {
            targetPanelId,
            newPanelId,
            filter,
            shouldAnimatePanelShift,
            placementIntent: !!placementIntent,
        })
        targetPanelId =
            panels.insertPanelRight(
                anchorPanelId,
                {
                    panelId: newPanelId,
                    filter,
                    empty: false,
                    deferContentUntilTransition: shouldSlide,
                    ...(placementIntent ? { pendingOpenPlacement: placementIntent } : {}),
                },
                {
                    animate: shouldAnimatePanelShift,
                    actionParams: {
                        ...(meta || {}),
                        manualSlide: shouldSlide,
                        openRightFromLeft,
                        prevCenterPanelId,
                    },
                },
            ) || newPanelId
        markPanelNavMetric('panel-inserted', {
            targetPanelId,
            newPanelId,
            filter,
            shouldAnimatePanelShift,
        })
    }

    if (shouldAnimatePanelShift) {
        flushSync(insertPanel)
    } else {
        insertPanel()
    }

    if (DEBUG()) {
        // eslint-disable-next-line no-console
        console.log('[openRightCore] inserted', {
            newPanelId,
            targetPanelId,
            actionParams: {
                openRightFromLeft,
                prevCenterPanelId,
                manualSlide: shouldSlide,
            },
        })
    }

    const finishTargetPanel = prepareTargetPanel(targetPanelId, true)

    if (shouldSlide && swiper) {
        prepareAnchorPanelDocking()
        const fn = () => {
            if (instantReplyNavEnabled) {
                markPanelNavTransitionEnd({
                    source: 'openRightSlideEnd',
                    targetPanelId,
                    filter,
                })
            }
            finishTargetPanel()
            swiper.off && swiper.off('slideChangeTransitionEnd', fn)
        }
        swiper.once
            ? swiper.once('slideChangeTransitionEnd', fn)
            : swiper.on?.('slideChangeTransitionEnd', fn)
        if (!isMobile && numPanels !== 1 && clickedRightPanel) {
            try {
                setManualSlide(true)
            } catch {}
            markPanelNavMetric('slide-requested', {
                method: 'slideNext',
                targetPanelId,
                filter,
            })
            swiper.slideNext(SWIPE_SPEED_FAST)
        } else {
            const targetIndex = panels.getIndexById(targetPanelId)
            const targetSwiperIndex =
                numPanels === 1 || isMobile ? targetIndex : Math.max(0, targetIndex - 1)
            // In one-panel/mobile, defer until React/Swiper renders the new slide
            if (numPanels === 1 || isMobile) {
                requestAnimationFrame(() => {
                    try {
                        swiper.virtual && swiper.virtual.update && swiper.virtual.update(true)
                    } catch {}
                    try {
                        setManualSlide(true)
                    } catch {}
                    markPanelNavMetric('slide-requested', {
                        method: 'slideTo',
                        targetPanelId,
                        targetIndex,
                        targetSwiperIndex,
                        filter,
                        deferred: true,
                    })
                    if (numPanels === 1 && !isMobile) {
                        slideToPanel(targetIndex, SWIPE_SPEED_SLOW)
                    } else {
                        swiper.slideTo(targetSwiperIndex, SWIPE_SPEED_FAST)
                    }
                })
            } else {
                try {
                    setManualSlide(true)
                } catch {}
                markPanelNavMetric('slide-requested', {
                    method: 'slideTo',
                    targetPanelId,
                    targetIndex,
                    targetSwiperIndex,
                    filter,
                })
                swiper.slideTo(targetSwiperIndex, SWIPE_SPEED_FAST)
            }
        }
    } else {
        markPanelNavMetric('slide-skipped', {
            reason: shouldSlide ? 'missing-swiper' : 'no-slide-needed',
            targetPanelId,
            filter,
        })
        markPanelNavTransitionEnd({
            source: 'openRightNoSwiperTransition',
            targetPanelId,
            filter,
        })
        setTimeout(finishTargetPanel, 250)
    }

    return { panelId: targetPanelId, existed: false as const }
}
